import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ButtonModule } from '@uzurv/afp/button';
import { SuspensionReason } from '@uzurv/clara';
import { DateUtil, PermissionsConstants, UzurvPipesModule } from '@uzurv/core';
import { AuthDataService } from '@uzurv/http';
import moment from 'moment-timezone';
import { MaterialModule } from 'projects/uzurv/src/app/material.module';
import { takeUntil } from 'rxjs';
import { BaseComponent } from '../../base/base.component';
import { RequiresPermissionsModule } from '../../directives';
import { GenericSearchSelectModule } from '../../generic-search-select';

export enum PersonSuspensionTypeEnum {
  RIDER = 'Rider',
  DRIVER = 'Driver'
}

export interface PersonSuspensionDialogData {
  personType: PersonSuspensionTypeEnum.RIDER | PersonSuspensionTypeEnum.DRIVER;
  suspensionReasons: Array<SuspensionReason>;
}

export interface PersonSuspensionDialogResults {
  suspensionReason: SuspensionReason;
  suspensionEffectiveDate: Date;
  suspensionDuration: number;
  suspensionDetailNotes: string;
}

@Component({
  selector: 'app-person-suspension',
  templateUrl: './person-suspension-dialog.html',
  styleUrls: ['./person-suspension-dialog.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    MaterialModule,
    MatIconModule,
    UzurvPipesModule,
    RequiresPermissionsModule,
    GenericSearchSelectModule,
    ReactiveFormsModule
  ]
})
export class PersonSuspensionDialog extends BaseComponent implements OnInit {
  suspensionForm: FormGroup<{
    suspensionReason: FormControl<SuspensionReason | null>;
    suspensionEffectiveDate: FormControl<Date | null>;
    suspensionDuration: FormControl<number | null>;
    suspensionDetailNotes: FormControl<string | null>;
  }>;
  suspensionDurations: Array<{ name: string; value: number }>;
  minDate = new Date();
  suspensionReasons: Array<SuspensionReason>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PersonSuspensionDialogData,
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<PersonSuspensionDialog>,
    private authDataService: AuthDataService
  ) {
    super();
    this.suspensionForm = this.fb.group({
      suspensionReason: new FormControl<SuspensionReason | null>(null, Validators.required),
      suspensionEffectiveDate: new FormControl<Date | null>(null, { validators: [Validators.required], updateOn: 'blur' }),
      suspensionDuration: new FormControl<number | null>(null, Validators.required),
      suspensionDetailNotes: new FormControl<string | null>(null, [Validators.required])
    });

    this.suspensionForm.controls.suspensionDetailNotes.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
      if (value && value !== value.trimStart()) {
        this.suspensionForm.controls.suspensionDetailNotes.setValue(value.trimStart(), { emitEvent: false });
      }
    });

    let hasPermanentSuspensionPermission: boolean;

    if (this.data.personType === PersonSuspensionTypeEnum.RIDER) {
      hasPermanentSuspensionPermission = this.authDataService.hasPermission({
        any: [PermissionsConstants.RIDER_SUSPENSION_PERMANENT_CREATE]
      });
    } else if (this.data.personType === PersonSuspensionTypeEnum.DRIVER) {
      hasPermanentSuspensionPermission = this.authDataService.hasPermission({
        any: [PermissionsConstants.DRIVER_SUSPENSION_PERMANENT_CREATE]
      });
    } else {
      hasPermanentSuspensionPermission = false;
    }

    if (hasPermanentSuspensionPermission) {
      this.suspensionReasons = this.data.suspensionReasons;
    } else {
      this.suspensionReasons = this.data.suspensionReasons.filter(reason => !reason.permanent);
    }
  }

  ngOnInit(): void {
    this.suspensionDurations = this.getSuspensionDurations();
  }

  handleSuspensionReasonSelected(event: any) {
    const suspensionReason = Array.isArray(event) ? event[0] : event;

    this.suspensionForm.patchValue({ suspensionReason });

    const durationControl = this.suspensionForm.controls.suspensionDuration;
    const effectiveDateControl = this.suspensionForm.controls.suspensionEffectiveDate;

    if (suspensionReason.permanent) {
      durationControl.removeValidators(Validators.required);
      durationControl.setValue(null);

      effectiveDateControl.removeValidators(Validators.required);
      effectiveDateControl.setValue(null);
    } else {
      durationControl.addValidators(Validators.required);
      effectiveDateControl.addValidators(Validators.required);
    }

    durationControl.updateValueAndValidity();
    effectiveDateControl.updateValueAndValidity();
  }

  handleCancel(): void {
    this.dialogRef.close();
  }

  handleSave(): void {
    if (this.suspensionForm.valid) {
      let effectiveDate = this.suspensionForm.value.suspensionEffectiveDate;

      if (effectiveDate) {
        const selectedDate = new Date(effectiveDate);

        // when suspension is today add submission timestamp else midnight
        if (DateUtil.isDateToday(moment(selectedDate))) {
          effectiveDate = new Date();
        } else {
          effectiveDate = new Date(effectiveDate);
          effectiveDate.setHours(0, 0, 0, 0);
        }
      }

      const result: PersonSuspensionDialogResults = {
        suspensionReason: this.suspensionForm.value.suspensionReason,
        suspensionEffectiveDate: effectiveDate,
        suspensionDuration: this.suspensionForm.value.suspensionDuration === 0 ? null : this.suspensionForm.value.suspensionDuration,
        suspensionDetailNotes: this.suspensionForm.value.suspensionDetailNotes
      };

      this.dialogRef.close(result);
    }
  }

  private getSuspensionDurations(): Array<{ name: string; value: number }> {
    const minDays = 1;
    const maxDays = 90;
    const durations: Array<{ name: string; value: number }> = [{ name: 'Indefinite', value: 0 }];

    for (let i = minDays; i <= maxDays; i++) {
      const hours = i * 24;
      const name = `${i} day${i > 1 ? 's' : ''} (${hours} hours)`;
      durations.push({ name, value: hours });
    }

    return durations;
  }
}
